/* Verkleinerte Fassungen von Spielerfotos — reine Funktionen, kein DOM.
 *
 * Das Original bleibt unverändert im Speicher (avatarPath zeigt darauf). Daneben
 * liegen zwei verkleinerte Fassungen im selben Ordner (avatars/<id>/), abgeleitet
 * aus dem Pfad des Originals — es ist kein Datenfeld nötig, und die Speicherregeln
 * für den Ordner avatars/ gelten auch für sie:
 *
 *   avatars/<id>/<name>.png               Original
 *   avatars/<id>/<name>.thumb.webp        Liste (30–60 px hoch angezeigt)
 *   avatars/<id>/<name>.md.webp           Info-Karte (130 × 173 px angezeigt)
 *
 * FORMAT: Die Fassungen behalten die Transparenz des Originals. Sie werden als
 * WebP geschrieben (mit Alphakanal), und wo der Browser WebP beim Canvas-Export
 * nicht liefert (Safari), als PNG; die Endung folgt dem tatsächlich gelieferten
 * Typ (encodeVariant). Bis 2026-10 waren es JPEG-Fassungen (.thumb.jpg / .md.jpg):
 * Das JPEG kennt keinen Alphakanal, die transparente Fläche wurde weiß. Solche
 * Altdateien bleiben liegen und werden nur noch genutzt, wenn es keine neuere
 * Fassung gibt (Reihenfolge VARIANT_FORMATS); "Fotos optimieren" kann sie neu erzeugen.
 *
 * Maße: Seitenverhältnis 3:4, wie die Anzeige im Projekt (PlayerAvatar.vue).
 *  - thumb 180 × 240: auch ein 60 px hoher Eintrag auf einem 3x-Bildschirm
 *    (180 px) wird noch scharf.
 *  - md 600 × 800: 130 × 173 px angezeigt braucht auf 3x etwa 390 × 520; die
 *    Reserve bis 600 × 800 deckt eine größere Darstellung ab, ohne die Datei
 *    unnötig groß zu machen.
 *
 * Zuschnitt: wie die Anzeige (object-fit: cover; object-position: top) —
 * waagerecht in der Mitte, senkrecht oben, damit bei Ganzkörperfotos der Kopf im
 * Bild bleibt.
 */

export const PHOTO_RATIO = 3 / 4;

export const VARIANTS = {
  thumb: { width: 180, height: 240, quality: 0.85 },
  md: { width: 600, height: 800, quality: 0.85 },
};

export const VARIANT_NAMES = Object.keys(VARIANTS);

/**
 * Dateiendungen abgeleiteter Fassungen in der Reihenfolge, in der sie bevorzugt
 * werden: webp und png haben Transparenz, jpg ist der Altbestand (weißer Hintergrund).
 */
export const VARIANT_FORMATS = ['webp', 'png', 'jpg'];

/** Ausgabeformate, die neue Fassungen haben dürfen (jpg nur noch als Altbestand). */
export const ENCODINGS = {
  'image/webp': { ext: 'webp', contentType: 'image/webp' },
  'image/png': { ext: 'png', contentType: 'image/png' },
};

const DERIVED = new RegExp(`\\.(${VARIANT_NAMES.join('|')})\\.(${VARIANT_FORMATS.join('|')})$`);

/** Ist das der Pfad einer abgeleiteten Fassung (und damit nie selbst ein Original)? */
export function isDerivedPath(path) {
  return DERIVED.test(path || '');
}

/**
 * Pfad der abgeleiteten Fassung in einem Format (Vorgabe webp). Für 'original',
 * unbekannte Namen, leere Pfade und Pfade, die schon eine abgeleitete Fassung sind,
 * kommt der Pfad unverändert zurück. Unbekannte Endungen fallen auf webp zurück.
 */
export function derivedPath(path, variant, ext = 'webp') {
  if (!path || !VARIANTS[variant] || isDerivedPath(path)) return path;
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  const base = dot > slash ? path.slice(0, dot) : path;
  return `${base}.${variant}.${VARIANT_FORMATS.includes(ext) ? ext : 'webp'}`;
}

/**
 * Alle möglichen Pfade einer Fassung, bevorzugte zuerst (webp, png, jpg-Altbestand).
 * Für 'original', unbekannte Namen, leere und schon abgeleitete Pfade: nur der Pfad selbst.
 */
export function derivedCandidates(path, variant) {
  const first = derivedPath(path, variant);
  if (first === path) return [path];
  return VARIANT_FORMATS.map((ext) => derivedPath(path, variant, ext));
}

/** Welche Fassung passt zu einer Anzeigehöhe in CSS-Pixeln? */
export function variantForSize(cssHeight) {
  return cssHeight > 100 ? 'md' : 'thumb';
}

/** Ausschnitt des Originals (3:4, waagerecht Mitte, senkrecht oben). */
export function coverRect(srcWidth, srcHeight) {
  if (srcWidth / srcHeight > PHOTO_RATIO) {
    const sw = Math.round(srcHeight * PHOTO_RATIO);
    return { sx: Math.round((srcWidth - sw) / 2), sy: 0, sw, sh: srcHeight };
  }
  const sh = Math.min(srcHeight, Math.round(srcWidth / PHOTO_RATIO));
  return { sx: 0, sy: 0, sw: srcWidth, sh };
}

/** Ausgabemaß; kleine Originale werden nicht hochgerechnet. */
export function outputSize(crop, variant) {
  const v = VARIANTS[variant];
  const scale = Math.min(1, crop.sw / v.width);
  return { width: Math.round(v.width * scale), height: Math.round(v.height * scale) };
}

/**
 * Format und Endung nach dem TATSÄCHLICH gelieferten Typ eines Blobs. canvas.toBlob
 * liefert still PNG, wenn der Browser den verlangten Typ nicht kann (Safari kennt kein
 * WebP) — deshalb zählt nie der verlangte, sondern immer der gelieferte Typ.
 * Unbekannte Typen (z. B. image/jpeg, das keine Transparenz hat) -> null.
 */
export function encodingFor(blobType) {
  return ENCODINGS[String(blobType || '').toLowerCase()] || null;
}

/**
 * Eine Fassung kodieren: erst WebP (mit Qualität), liefert der Browser kein WebP,
 * PNG. encode(type, quality) -> Promise<Blob|null> (im Browser canvas.toBlob).
 * Ergebnis { blob, ext, contentType }. Wirft 'imageEncodeFailed', wenn weder WebP
 * noch PNG geliefert wird — nie ein JPEG und nie eine Vollfarbe statt Transparenz.
 */
export async function encodeVariant(encode, quality) {
  const webp = await encode('image/webp', quality);
  const webpEnc = webp && encodingFor(webp.type);
  if (webpEnc && webpEnc.ext === 'webp') return { blob: webp, ...webpEnc };

  const png = await encode('image/png');
  const pngEnc = png && encodingFor(png.type);
  if (pngEnc && pngEnc.ext === 'png') return { blob: png, ...pngEnc };

  throw new Error('imageEncodeFailed');
}

/**
 * Aus der Antwort einer Mehrfachabfrage [Fassung 1, Fassung 2, …, Original] (je
 * { signedUrl, error }) die Adresse wählen: die erste vorhandene Fassung in der
 * Reihenfolge der Abfrage, sonst das Original (der letzte Eintrag), sonst nichts.
 * Fehlt jede Fassung (alte Fotos), ist das kein Fehler. `index` ist die Stelle der
 * gewählten Fassung, `used` 'variant' | 'original' | null.
 */
export function pickSigned(entries) {
  const list = entries || [];
  const ok = (e) => !!(e && e.signedUrl && !e.error);
  for (let i = 0; i < list.length - 1; i++) {
    if (ok(list[i])) return { url: list[i].signedUrl, used: 'variant', index: i };
  }
  const original = list[list.length - 1];
  if (list.length && ok(original)) return { url: original.signedUrl, used: 'original', index: list.length - 1 };
  return { url: null, used: null, index: -1 };
}

/**
 * Zustand der Fassungen EINES Namens einer Datei nach den vorhandenen Endungen
 * ({ webp, png, jpg } je true/false): 'current' (webp oder png, mit Transparenz),
 * 'legacy' (nur die alte jpg-Fassung, weißer Hintergrund) oder 'missing'.
 */
export function variantState(exists) {
  const e = exists || {};
  if (e.webp || e.png) return 'current';
  if (e.jpg) return 'legacy';
  return 'missing';
}

/**
 * Zustand eines Fotos aus den Zuständen seiner Fassungen: 'missing', sobald eine
 * fehlt; sonst 'legacy', sobald eine nur als jpg da ist; sonst 'current'.
 */
export function photoState(states) {
  if (states.includes('missing')) return 'missing';
  if (states.includes('legacy')) return 'legacy';
  return 'current';
}

/**
 * Welche Dateien eines Spielers sollen verkleinerte Fassungen bekommen, und welche?
 * avatarPath: beide (Liste und Info-Karte). bioPhotoPath (Altbestand): nur md, denn
 * es erscheint nur in der Info-Karte. Eingebettete Bilder (avatar/bioPhoto als
 * Data-URL) haben keine Datei und kommen hier nicht vor. Abgeleitete Pfade und
 * doppelte Pfade werden übergangen.
 */
export function photoTargets(player) {
  const out = [];
  const seen = new Set();
  const add = (path, names) => {
    if (!path || isDerivedPath(path) || seen.has(path)) return;
    seen.add(path);
    out.push({ path, names });
  };
  add(player && player.avatarPath, VARIANT_NAMES);
  add(player && player.bioPhotoPath, ['md']);
  return out;
}
